import { PlayerAction } from 'constants/game';
import { resolveViewerBetTiers } from 'constants/betting';
import { BetTiersView, SideShowPendingView } from 'types/game';
import { useSound } from 'context/SoundProvider';
import { useCurrency } from 'hooks/useCurrency';
import { isSharEnabled } from 'lib/shar';
import btnPack from 'assets/action-buttons/btn-pack.png';
import btnRaise from 'assets/action-buttons/btn-raise.png';
import btnChaal from 'assets/action-buttons/btn-chaal.png';
import btnShow from 'assets/action-buttons/btn-show.png';

interface DesktopActionPanelProps {
  phase: string;
  waitingForPlayers: boolean;
  seatedCount?: number;
  eligibleCount?: number;
  bootAmount?: number;
  disabled: boolean;
  isMyTurn: boolean;
  isSeen: boolean;
  mySeatNo?: number;
  activePlayers: number;
  currentStake: number;
  currentBet?: number;
  isDealing?: boolean;
  handStarting?: boolean;
  interRoundActive?: boolean;
  interRoundCountdown?: number;
  interRoundWinnerName?: string;
  interRoundPotAmount?: number;
  interRoundHandCategory?: string;
  winPhase?: string;
  isFolded?: boolean;
  centerHandTimerActive?: boolean;
  sharAmount?: number;
  sharVote?: 'accept' | 'decline';
  sideShowPending?: SideShowPendingView | null;
  legalActions?: string[];
  betTiers?: BetTiersView;
  maxBet?: number;
  potLimit?: number;
  compactFooter?: boolean;
  compactPortrait?: boolean;
  onAction: (action: PlayerAction, amount?: number) => void;
}

type OctroTone = 'pack' | 'raise' | 'chaal' | 'show' | 'side' | 'accept';

const OCTRO_BTN_ART: Record<OctroTone, string> = {
  pack: btnPack,
  side: btnShow,
  show: btnShow,
  raise: btnRaise,
  chaal: btnChaal,
  accept: btnPack,
};

function hasAction(legal: string[] | undefined, action: string, phase: string): boolean {
  if (phase !== 'betting') return false;
  return Boolean(legal?.includes(action));
}

function TableLimitsIdle({
  bootLabel,
  maxLabel,
  potLabel,
}: {
  bootLabel: string;
  maxLabel: string;
  potLabel: string;
}) {
  return (
    <div className="tp-octro-limits tp-octro-limits-idle" aria-label="Table limits">
      <div className="tp-octro-limits-row">
        <span className="tp-octro-limit-boot">{bootLabel}</span>
        <span className="tp-octro-limit-pot">{potLabel}</span>
      </div>
      <div className="tp-octro-limits-row tp-octro-limits-row-center">
        <span className="tp-octro-limit-max">{maxLabel}</span>
      </div>
    </div>
  );
}

function OctroButton({
  tone,
  label,
  labelLines,
  disabled,
  highlight,
  onClick,
}: {
  tone: OctroTone;
  label?: string;
  labelLines?: string[];
  disabled?: boolean;
  highlight?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`tp-octro-btn tp-octro-btn-${tone}${labelLines?.length ? ' tp-octro-btn-stacked-text' : ''}${highlight ? ' tp-octro-btn-active' : ''}`}
    >
      <img
        src={OCTRO_BTN_ART[tone]}
        alt=""
        aria-hidden="true"
        className="tp-octro-btn-art"
        draggable={false}
      />
      {labelLines?.length ? (
        <span className="tp-octro-btn-label tp-octro-btn-label-stack">
          {labelLines.map((line, index) => (
            <span
              key={`${line}-${index}`}
              className={`tp-octro-btn-text-line${index === 0 ? ' tp-octro-btn-text-amount' : ' tp-octro-btn-text-action'}`}
            >
              {line}
            </span>
          ))}
        </span>
      ) : (
        <span className="tp-octro-btn-label">{label}</span>
      )}
    </button>
  );
}

export default function DesktopActionPanel({
  phase,
  waitingForPlayers,
  disabled,
  isMyTurn,
  isSeen,
  mySeatNo,
  activePlayers,
  currentStake,
  interRoundActive,
  interRoundCountdown,
  winPhase,
  isFolded,
  isDealing = false,
  handStarting = false,
  centerHandTimerActive,
  sharAmount = 0,
  sharVote,
  sideShowPending,
  legalActions,
  betTiers,
  maxBet,
  potLimit,
  bootAmount = 0,
  onAction,
}: DesktopActionPanelProps) {
  const { play } = useSound();
  const { formatStake } = useCurrency();
  const sharEnabled = isSharEnabled(sharAmount);
  const sharPhaseActive = phase === 'shar' && sharEnabled;
  const resolvedMaxBet = Math.max(0, Number(maxBet) || 0);
  const resolvedPotLimit = Math.max(0, Number(potLimit) || resolvedMaxBet * 4);
  const resolvedBoot = Math.max(0, Number(bootAmount) || 0);

  const click = (action: PlayerAction, amount?: number) => {
    if (action === 'fold') play('pack');
    else if (action === 'see') play('flip');
    else if (
      action !== 'blind' &&
      action !== 'chaal' &&
      action !== 'raise' &&
      action !== 'show' &&
      action !== 'side_show'
    ) {
      play('click');
    }
    onAction(action, amount);
  };

  const cardsDealtForActions = phase === 'betting' && !isDealing;
  const onTurnBetting = cardsDealtForActions && isMyTurn;
  const canShow = (hasAction(legalActions, 'show', phase) || onTurnBetting) && activePlayers === 2;
  const canBlind = hasAction(legalActions, 'blind', phase) || (onTurnBetting && !isSeen);
  const canChaal = hasAction(legalActions, 'chaal', phase) || (onTurnBetting && isSeen);
  const canSideShow = hasAction(legalActions, 'side_show', phase);
  const canFold = hasAction(legalActions, 'fold', phase) || onTurnBetting;

  const level = Math.max(1, currentStake);
  const fallbackTiers = resolveViewerBetTiers(isSeen, level, {
    includeBlind: !isSeen,
    maxBet: resolvedMaxBet || undefined,
  });
  const tiers: BetTiersView = {
    blind: betTiers?.blind ?? fallbackTiers.blind,
    chaal: betTiers?.chaal ?? fallbackTiers.chaal,
    show: betTiers?.show ?? fallbackTiers.show,
    sideShow: betTiers?.sideShow,
  };

  const blind1x = tiers.blind?.[0] ?? level;
  const blind2x = tiers.blind?.[1] ?? level * 2;
  const chaal1x = tiers.chaal?.[0] ?? level * 2;
  const chaal2x = tiers.chaal?.[1] ?? level * 4;
  const showAmount = tiers.show ?? (isSeen ? level * 2 : level);
  const sideShowAmount = tiers.sideShow;
  const showSideShowButton = activePlayers >= 3;

  const showBlindControls = !isSeen;
  const primaryBetAmount = showBlindControls ? blind1x : chaal1x;
  const raiseAmount = showBlindControls ? blind2x : chaal2x;
  const primaryLabel = showBlindControls ? 'BLIND' : 'CHAAL';
  const canPrimaryBet = showBlindControls ? canBlind : canChaal;
  const canRaiseBet = canPrimaryBet && raiseAmount > primaryBetAmount;

  const isSideShowTarget =
    sideShowPending != null &&
    mySeatNo != null &&
    Number(sideShowPending.targetSeat) === Number(mySeatNo);
  const isSideShowRequester =
    sideShowPending != null &&
    mySeatNo != null &&
    Number(sideShowPending.requesterSeat) === Number(mySeatNo);

  const handTimerActive = Boolean(
    handStarting ||
    centerHandTimerActive ||
    interRoundActive ||
    (interRoundCountdown != null && interRoundCountdown > 0)
  );

  const formatLimit = (value: number) =>
    Math.max(0, Math.floor(Number(value) || 0)).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  const bootLabel = `Boot Value: ${formatLimit(resolvedBoot)}`;
  const maxLabel = `Max Bet: ${formatLimit(resolvedMaxBet)}`;
  const potLabel = `Pot Limit: ${formatLimit(resolvedPotLimit)}`;
  const idleLimits = (
    <TableLimitsIdle bootLabel={bootLabel} maxLabel={maxLabel} potLabel={potLabel} />
  );

  if (handTimerActive && !sharPhaseActive) {
    return (
      <footer className="tp-table-footer tp-table-footer-betting tp-table-footer-idle tp-octro-footer-fixed">
        {idleLimits}
      </footer>
    );
  }

  const betActionsLocked =
    disabled ||
    isFolded ||
    phase !== 'betting' ||
    !isMyTurn ||
    isDealing ||
    handTimerActive;

  const bettingBlocked = isFolded || phase !== 'betting' || isDealing || handTimerActive;
  const sideShowEligible = canSideShow && sideShowAmount != null && showSideShowButton;
  const sideShowClickable = sideShowEligible && isMyTurn && !bettingBlocked;

  const renderActionGrid = () => {
    const canHighlight = (allowed: boolean) => allowed && isMyTurn && !betActionsLocked;
    const rightIsShow = canShow && activePlayers === 2;
    const raiseLines = [formatStake(raiseAmount), `2X ${primaryLabel}`];
    const primaryLines = [formatStake(primaryBetAmount), primaryLabel];

    return (
      <footer className="tp-table-footer tp-table-footer-betting tp-table-footer-active tp-octro-footer-fixed">
        <div className="tp-octro-actions">
          <div className="tp-octro-actions-inner">
            <OctroButton
              tone="pack"
              label="PACK"
              disabled={betActionsLocked || !canFold}
              onClick={() => click('fold')}
            />

            <div className="tp-octro-stack">
              <OctroButton
                tone="raise"
                labelLines={raiseLines}
                highlight={canHighlight(canRaiseBet)}
                disabled={betActionsLocked || !canRaiseBet}
                onClick={() => click(showBlindControls ? 'blind' : 'chaal', raiseAmount)}
              />
              <OctroButton
                tone="chaal"
                labelLines={primaryLines}
                highlight={canHighlight(canPrimaryBet)}
                disabled={betActionsLocked || !canPrimaryBet}
                onClick={() => click(showBlindControls ? 'blind' : 'chaal', primaryBetAmount)}
              />
            </div>

            {rightIsShow ? (
              <OctroButton
                tone="show"
                label="SHOW"
                highlight={canHighlight(canShow)}
                disabled={betActionsLocked || !canShow}
                onClick={() => click('show', showAmount)}
              />
            ) : (
              <OctroButton
                tone="side"
                labelLines={['SIDE', 'SHOW']}
                highlight={sideShowClickable}
                disabled={!sideShowClickable || betActionsLocked}
                onClick={() => {
                  if (sideShowAmount == null || !sideShowClickable) return;
                  click('side_show', sideShowAmount);
                }}
              />
            )}
          </div>
        </div>
      </footer>
    );
  };

  if (sharPhaseActive && !sharVote) {
    return (
      <footer className="tp-table-footer tp-table-footer-betting tp-table-footer-active tp-octro-footer-fixed">
        <div className="tp-octro-actions tp-octro-actions-dual">
          <div className="tp-octro-actions-inner">
            <OctroButton
              tone="accept"
              label="ACCEPT"
              disabled={disabled}
              onClick={() => click('shar_accept')}
            />
            <OctroButton
              tone="show"
              label="DECLINE"
              disabled={disabled}
              onClick={() => click('shar_decline')}
            />
          </div>
        </div>
      </footer>
    );
  }

  if (sharPhaseActive && sharVote) {
    return (
      <footer className="tp-table-footer tp-table-footer-betting tp-table-footer-idle tp-octro-footer-fixed">
        {idleLimits}
      </footer>
    );
  }

  if (isSideShowRequester && sideShowPending) {
    return (
      <footer className="tp-table-footer tp-table-footer-betting tp-table-footer-idle tp-octro-footer-fixed">
        {idleLimits}
      </footer>
    );
  }

  if (isSideShowTarget && sideShowPending) {
    return (
      <footer className="tp-table-footer tp-table-footer-betting tp-table-footer-active tp-octro-footer-fixed">
        <div className="tp-octro-actions tp-octro-actions-dual">
          <div className="tp-octro-actions-inner">
            <OctroButton
              tone="accept"
              label="ACCEPT"
              disabled={disabled}
              onClick={() => click('side_show_accept')}
            />
            <OctroButton
              tone="show"
              label="REJECT"
              disabled={disabled}
              onClick={() => click('side_show_reject')}
            />
          </div>
        </div>
      </footer>
    );
  }

  const hideBetButtons =
    waitingForPlayers ||
    !isMyTurn ||
    isFolded ||
    phase === 'waiting' ||
    phase === 'showdown' ||
    phase === 'completed' ||
    phase === 'boot' ||
    phase === 'dealing' ||
    winPhase === 'showdown' ||
    winPhase === 'collect' ||
    winPhase === 'crown' ||
    Boolean(sideShowPending);

  if (hideBetButtons) {
    return (
      <footer className="tp-table-footer tp-table-footer-betting tp-table-footer-idle tp-octro-footer-fixed">
        {idleLimits}
      </footer>
    );
  }

  return renderActionGrid();
}
